import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { collection, query, where, getDocs, doc, setDoc, getDoc, deleteDoc, writeBatch, onSnapshot, serverTimestamp } from 'firebase/firestore';
import { getAuth, createUserWithEmailAndPassword, sendPasswordResetEmail } from 'firebase/auth';
import { db, auth, firebaseConfig } from '../firebase';
import { ROLE, ROLE_LABEL, ASSIGNABLE_ROLES, normaliseRole } from '../roles';
import { useT } from '../i18n.jsx';
import '../theme-palette.css';
import { Users, Search, Edit2, Shield, Plus, Loader, LayoutGrid, Trash2, Download, Upload, BookOpen, Briefcase, ChevronUp, ChevronDown, Filter, ArrowUpDown } from 'lucide-react';
import { useOutletContext } from 'react-router-dom';
import Papa from 'papaparse';
import HeaderActionsPortal from './HeaderActionsPortal';

// Both collections now use the same field name as well as the same values: `role`. They used to
// differ only in casing (user_permissions.Role), which never failed loudly but made any query
// against the wrong spelling return zero rows instead of an error -- see src/roles.js.

const StaffMgt = () => {
  const { t } = useT();
  // The role id is the key; roles.js keeps the English label as the fallback.
  const roleWord = (r) => t(`roles.${r}`, {}, ROLE_LABEL[r] || r);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [studentMap, setStudentMap] = useState({});

  // Table State
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [filterConfig, setFilterConfig] = useState({});
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [showFilterPopover, setShowFilterPopover] = useState(null);

  // Modal State
  const [showUserModal, setShowUserModal] = useState(false);
  const [isEditingUser, setIsEditingUser] = useState(false);
  const [userForm, setUserForm] = useState({
    email: '', password: '', firstName: '', lastName: '', chineseName: '', systemRole: ROLE.TEACHER, gender: 'Male', initial: '', rank: '', jobRole: 'Teaching Staff', hasLoginAccess: false
  });

  const { activeSchoolId, isMobile, userProfile } = useOutletContext();
  const [dropdownConfig, setDropdownConfig] = useState({ edbRanks: [], jobRoles: [] });


  const requestAccountAction = (action, email, password, type, systemRole = '') => {
    return new Promise(async (resolve, reject) => {
      try {
        const docRef = doc(collection(db, 'schools', activeSchoolId, '_account_requests'));
        await setDoc(docRef, {
          email,
          password,
          type,
          // The actual system role (admin/teacher/staff/student). Without it the function could only
          // tell staff from student and wrote every staff account as Role 'Teacher' -- which is the
          // field Firestore rules read for admin checks, so an administrator created here could log
          // in but was refused every admin-gated write.
          systemRole,
          action,
          status: 'PENDING',
          createdAt: serverTimestamp()
        });

        const unsubscribe = onSnapshot(docRef, (snap) => {
          const data = snap.data();
          if (data && data.status === 'SUCCESS') {
            unsubscribe();
            deleteDoc(docRef).catch(console.error);
            resolve(data.message);
          } else if (data && data.status === 'ERROR') {
            unsubscribe();
            deleteDoc(docRef).catch(console.error);
            reject(new Error(data.message));
          }
        });
      } catch (err) {
        reject(err);
      }
    });
  };

  useEffect(() => {
    if (activeSchoolId) fetchUsers();
  }, [activeSchoolId]);

  useEffect(() => {
    if (!activeSchoolId) return;
    const unsub = onSnapshot(doc(db, 'schools', activeSchoolId, 'metadata', 'system_settings'), async (docSnap) => {
      if (docSnap.exists() && docSnap.data().dropdowns) {
        setDropdownConfig(docSnap.data().dropdowns);
      } else {
        getDoc(doc(db, 'metadata', 'system_settings')).then(globalSnap => {
          if (globalSnap.exists() && globalSnap.data().dropdowns) {
            setDropdownConfig(globalSnap.data().dropdowns);
          }
        });
      }
    });
    return () => unsub();
  }, [activeSchoolId]);

  const fetchUsers = async () => {
    if (!activeSchoolId) return;
    try {
      const [metaSnap, staffSnap] = await Promise.all([
        getDoc(doc(db, 'metadata', `student_index_${activeSchoolId}`)),
        getDocs(query(collection(db, 'schools', activeSchoolId, 'staff_profiles')))
      ]);

      const sMap = {};
      if (metaSnap.exists()) {
        const list = metaSnap.data().list || [];
        list.forEach(s => {
          sMap[s.regno] = s;
        });
      }
      setStudentMap(sMap);

      const fetchedUsers = [];
      staffSnap.forEach(doc => {
        const d = doc.data();
        const accountId = doc.id;
        const email = d.email || accountId; // Support legacy where email was id
        // Canonicalised on read so the filter tabs, badges and edit form all speak one vocabulary
        // whether or not the stored document has been migrated yet.
        let role = normaliseRole(d.role || d.Role || ROLE.NON_TEACHING);
        let display = '';
        let staffInitial = d.initial || '';
        let studentInfo = null;

        if (d.firstName || d.lastName) {
          display = `${d.lastName || ''} ${d.firstName || ''}`.trim();
        } else {
          display = email.split('@')[0].toUpperCase();
        }

        fetchedUsers.push({
          id: accountId,
          email: email,
          role: role,
          subRoles: d.subRoles || d.SubRoles || [],
          displayName: display,
          staffInitial: staffInitial,
          studentInfo: studentInfo,
          allowedModules: d.allowedModules || [],
          viewAccess: d.viewAccess || d.ViewAccess || '',
          firstName: d.firstName || '',
          lastName: d.lastName || '',
          chineseName: d.chineseName || '',
          gender: d.gender || 'Male',
          initial: d.initial || '',
          rank: d.rank || '',
          jobRole: d.jobRole || 'Teaching Staff',
          hasLoginAccess: !!d.hasLoginAccess
        });
      });

      setUsers(fetchedUsers);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching users:", error);
      setLoading(false);
    }
  };

  const handleOpenAddUser = () => {
    setIsEditingUser(false);
    setUserForm({
      email: '', password: '', firstName: '', lastName: '', chineseName: '',
      systemRole: ROLE.TEACHER, gender: 'Male', initial: '', rank: '', jobRole: 'Teaching Staff', hasLoginAccess: false
    });
    setShowUserModal(true);
  };

  const handleOpenEditUser = (u) => {
    setIsEditingUser(true);
    setUserForm({
      email: u.id,
      password: '',
      firstName: u.firstName,
      lastName: u.lastName,
      chineseName: u.chineseName || '',
      systemRole: normaliseRole(u.role),
      gender: u.gender || 'Male',
      initial: u.initial || '',
      rank: u.rank || '',
      jobRole: u.jobRole || 'Teaching Staff',
      hasLoginAccess: !!u.hasLoginAccess
    });
    setShowUserModal(true);
  };

  const handleDeleteUser = async (email) => {
    if (!window.confirm(t('staffMgt.deleteConfirm', { email }))) return;
    setLoading(true);
    try {
      await requestAccountAction('Delete Account', email, '', '');
      fetchUsers();
    } catch (err) {
      alert(t('staffMgt.deleteFailed', { message: err.message }));
      setLoading(false);
    }
  };

  const handleSendResetEmail = async () => {
    if (!userForm.email) return;
    try {
      const primaryAuth = getAuth();
      await sendPasswordResetEmail(primaryAuth, userForm.email);
      alert(t('staffMgt.resetSent', { email: userForm.email }));
    } catch (err) {
      alert(t('staffMgt.resetFailed', { message: err.message }));
    }
  };

  const handleSaveUser = async (e) => {
    e.preventDefault();
    if (!userForm.email) return;

    const validGenders = ['Male', 'Female', ''];
    if (!ASSIGNABLE_ROLES.includes(userForm.systemRole)) return alert(t('staffMgt.invalidRole'));
    if (!validGenders.includes(userForm.gender)) return alert(t('staffMgt.invalidGender'));

    const jobRoleValid = (dropdownConfig?.jobRoles || []).some(r => r.code === userForm.jobRole || r.nameEn === userForm.jobRole || r.name === userForm.jobRole);
    if (userForm.jobRole && !jobRoleValid && userForm.jobRole !== 'Teaching Staff') return alert(t('staffMgt.invalidJobRole'));
    
    const rankValid = !userForm.rank || (dropdownConfig?.edbRanks || []).some(r => {
      const rStr = typeof r === 'string' ? r : (r.code || r.nameEn || r.name);
      return rStr === userForm.rank;
    });
    if (!rankValid) return alert(t('staffMgt.invalidRank'));

    setLoading(true);
    try {
      if (userForm.hasLoginAccess) {
        if (!isEditingUser && !userForm.password) {
          setLoading(false);
          return alert(t('staffMgt.passwordRequired'));
        }
        if (userForm.password) {
          if (userForm.password.length < 6) {
            setLoading(false);
            return alert(t('staffMgt.passwordTooShort'));
          }
          try {
            await requestAccountAction(isEditingUser ? 'Update' : 'Add', userForm.email, userForm.password, userForm.systemRole === 'student' ? 'student' : 'staff', userForm.systemRole);
          } catch (authErr) {
            throw new Error("Account Action Failed: " + authErr.message);
          }
        }
      } else {
        try {
          await requestAccountAction('Delete Account', userForm.email, '', '');
        } catch (e) {}
      }

      // Profile Data
      const profRef = doc(db, 'schools', activeSchoolId, 'staff_profiles', userForm.email.toLowerCase());
      await setDoc(profRef, {
        email: userForm.email.toLowerCase(),
        role: userForm.systemRole,
        firstName: userForm.firstName,
        lastName: userForm.lastName,
        chineseName: userForm.chineseName,
        gender: userForm.gender,
        initial: userForm.initial,
        rank: userForm.rank,
        jobRole: userForm.jobRole,
        schoolId: activeSchoolId,
        hasLoginAccess: userForm.hasLoginAccess
      }, { merge: true });

      // staff_profiles.role is what the UI reads; user_permissions.role is what Firestore rules
      // read. Keeping them in step here (rather than only inside the provisioning function) matters
      // because that function only runs when a password is supplied -- so changing somebody's role
      // without resetting their password would otherwise never reach the rules at all.
      if (userForm.hasLoginAccess) {
        const permRef = doc(db, 'schools', activeSchoolId, 'user_permissions', userForm.email.toLowerCase());
        await setDoc(permRef, { Role: userForm.systemRole }, { merge: true });
      }

      // Cascading inventory update
      if (isEditingUser) {
        const oldUser = users.find(u => u.id === userForm.email.toLowerCase());
        const oldInitial = oldUser ? oldUser.initial : null;
        if (oldInitial && userForm.initial && oldInitial !== userForm.initial) {
          const iSnap = await getDocs(query(collection(db, 'schools', activeSchoolId, 'inventory'), where('assignedTo', '==', oldInitial)));
          if (!iSnap.empty) {
            const batch = writeBatch(db);
            iSnap.forEach(docSnap => batch.update(docSnap.ref, { assignedTo: userForm.initial }));
            await batch.commit();
          }
        }
      }

      setShowUserModal(false);
      fetchUsers();
    } catch (err) {
      alert(t('staffMgt.saveFailed', { message: err.message }));
      setLoading(false);
    }
  };

  const exportUsersCSV = () => {
    const csvData = users.map(u => ({
      Email: u.id,
      'Last Name': u.lastName || '',
      'First Name': u.firstName || '',
      'Chinese Name': u.chineseName || '',
      Role: u.role,
      Gender: u.gender || '',
      Initial: u.initial || '',
      'Job Role': u.jobRole || '',
      'EDB Rank': u.rank || '',
      'Enable Login': u.hasLoginAccess ? 'Yes' : 'No'
    }));
    const csv = Papa.unparse(csvData);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.setAttribute("download", "system_accounts.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleImportCSV = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (evt) => {
      const buffer = evt.target.result;
      const arr = new Uint8Array(buffer);
      let encoding = 'utf-8';
      try {
        new TextDecoder('utf-8', { fatal: true }).decode(arr);
      } catch (err) {
        encoding = 'Big5';
      }

      Papa.parse(file, {
        header: true,
        skipEmptyLines: true,
        encoding: encoding,
        complete: async (results) => {
          setLoading(true);
        try {
          const errors = [];
          const validGenders = ['Male', 'Female', ''];

          
          const getJobRoleCode = (input) => {
            if (!input) return 'Teaching Staff';
            const str = String(input).trim();
            const role = (dropdownConfig?.jobRoles || []).find(r => r.code === str || r.nameEn === str || r.name === str);
            return role ? (role.code || str) : null;
          };

          const getRankCode = (input) => {
            if (!input) return '';
            const str = String(input).trim();
            const rank = (dropdownConfig?.edbRanks || []).find(r => {
              const rStr = typeof r === 'string' ? r : (r.code || r.nameEn || r.name);
              return rStr === str;
            });
            return rank ? str : null;
          };

          results.data.forEach((row, index) => {
            const email = row['Email']?.trim().toLowerCase();
            if (!email) return;

            // Accepts the legacy spellings ('admin', 'staff') as well as the canonical ones, but
            // anything it cannot resolve is rejected outright rather than imported as free text --
            // which is how arbitrary job titles used to end up in the role field.
            const role = normaliseRole(row['Role'] || ROLE.NON_TEACHING);
            if (!ASSIGNABLE_ROLES.includes(role) || role === ROLE.DENIED) {
              errors.push(t('staffMgt.rowInvalidRole', {
                row: index + 1, email, value: row['Role'] || '', allowed: ASSIGNABLE_ROLES.join(', '),
              }));
            }

            const gender = row['Gender'] || '';
            if (!validGenders.includes(gender)) {
              errors.push(t('staffMgt.rowInvalidGender', { row: index + 1, email, value: gender }));
            }

            const jobRoleInput = row['Job Role'];
            const jobRoleCode = getJobRoleCode(jobRoleInput);
            if (!jobRoleCode) {
              errors.push(t('staffMgt.rowInvalidJobRole', { row: index + 1, email, value: jobRoleInput }));
            }
            
            const rankInput = row['EDB Rank'] || row['Rank'];
            if (rankInput) {
              const rankCode = getRankCode(rankInput);
              if (!rankCode) {
                errors.push(t('staffMgt.rowInvalidRank', { row: index + 1, email, value: rankInput }));
              }
            }
          });

          if (errors.length > 0) {
            setLoading(false);
            alert(t('staffMgt.validationFailed') + '\n\n' + errors.slice(0, 10).join('\n')
              + (errors.length > 10 ? '\n' + t('staffMgt.andMoreErrors', { count: errors.length - 10 }) : ''));
            return;
          }

          const batch = writeBatch(db);
          let count = 0;

          for (const row of results.data) {
            const email = row['Email']?.trim().toLowerCase();
            if (!email) continue;
            
            const enableLoginStr = (row['Enable Login'] || '').toLowerCase().trim();
            const hasLoginAccess = enableLoginStr === 'yes' || enableLoginStr === 'true' || enableLoginStr === '1';

            // Canonicalised once here, so the account request, the profile write and the
            // student/staff comparison below all agree. The validation pass has already rejected
            // any row whose Role could not be resolved, so this cannot silently become 'denied'.
            const Role = normaliseRole(row['Role'] || ROLE.NON_TEACHING);
            const firstName = row['First Name'] || '';
            const lastName = row['Last Name'] || '';
            const chineseName = row['Chinese Name'] || '';
            const rawPassword = row['Password'];
            
            let passwordToSet = null;
            if (rawPassword && rawPassword !== '*' && rawPassword.trim() !== '') {
              passwordToSet = rawPassword.trim();
            }

            const existingProfile = users.find(u => u.email === email || u.id === email);
            const isNew = !existingProfile;

            if (isNew) {
              if (hasLoginAccess) {
                 await requestAccountAction('Add', email, passwordToSet || '123456', Role === ROLE.STUDENT ? 'student' : 'staff', Role);
              }
            } else {
              if (hasLoginAccess && !existingProfile.hasLoginAccess) {
                 await requestAccountAction('Add', email, passwordToSet || '123456', Role === ROLE.STUDENT ? 'student' : 'staff', Role);
              } else if (!hasLoginAccess && existingProfile.hasLoginAccess) {
                 try {
                   await requestAccountAction('Delete Account', email, '', '');
                 } catch (e) {}
              } else if (hasLoginAccess && passwordToSet) {
                 await requestAccountAction('Update', email, passwordToSet, Role === 'student' ? 'student' : 'staff');
              }
            }

            let accountId = email;
            try {
              const indexDoc = await getDoc(doc(db, 'schools', activeSchoolId, 'email_index', accountId));
              if (indexDoc.exists() && indexDoc.data().accountId) {
                accountId = indexDoc.data().accountId;
              }
            } catch(e) {}

            const profRef = doc(db, 'schools', activeSchoolId, 'staff_profiles', accountId);
            batch.set(profRef, {
              email: email,
              role: Role,
              firstName: firstName,
              lastName: lastName,
              chineseName: chineseName,
              gender: row['Gender'] || '',
              initial: row['Initial'] || row['Staff Initial'] || '',
              rank: row['EDB Rank'] || row['Rank'] || '',
              jobRole: getJobRoleCode(row['Job Role']),
              schoolId: activeSchoolId,
              hasLoginAccess: hasLoginAccess
            }, { merge: true });

            count++;
          }

          await batch.commit();
          alert(t('staffMgt.imported', { count }));
          fetchUsers();
        } catch (err) {
          alert(t('staffMgt.importFailed', { message: err.message }));
          setLoading(false);
        }
      },
      error: (err) => {
        alert(t('staffMgt.parseFailed', { message: err.message }));
      }
    });
    };
    reader.readAsArrayBuffer(file);
    // Reset input
    e.target.value = null;
  };

  const handleBatchDelete = async () => {
    if (selectedUsers.length === 0) return;
    if (!window.confirm(t('staffMgt.deleteManyConfirm', { count: selectedUsers.length }))) return;
    setLoading(true);
    try {
      for (const email of selectedUsers) {
        await requestAccountAction('Delete Account', email, '', '');
      }
      setSelectedUsers([]);
      fetchUsers();
    } catch (err) {
      alert(t('staffMgt.deleteManyFailed', { message: err.message }));
      setLoading(false);
    }
  };

  const toggleSelectAll = () => {
    if (selectedUsers.length === sortedUsers.length && sortedUsers.length > 0) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(sortedUsers.map(u => u.id));
    }
  };

  const toggleSelectUser = (id) => {
    if (selectedUsers.includes(id)) {
      setSelectedUsers(selectedUsers.filter(userId => userId !== id));
    } else {
      setSelectedUsers([...selectedUsers, id]);
    }
  };

  const renderHeader = (label, key, sortable = true) => {
    return (
      <th style={{ padding: '1rem 1.5rem', borderBottom: '1px solid var(--color-border)', fontWeight: '600', color: 'var(--color-text-muted)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <span 
            style={{ cursor: sortable ? 'pointer' : 'default', userSelect: 'none', display: 'flex', alignItems: 'center' }}
            onClick={() => {
              if (!sortable) return;
              let direction = 'asc';
              if (sortConfig.key === key && sortConfig.direction === 'asc') direction = 'desc';
              setSortConfig({ key, direction });
            }}
          >
            {label}
            {sortable && (
              sortConfig.key === key ? (
                sortConfig.direction === 'asc' 
                  ? <ChevronUp size={14} style={{ display: 'inline', marginLeft: '4px', color: 'var(--color-primary)' }} /> 
                  : <ChevronDown size={14} style={{ display: 'inline', marginLeft: '4px', color: 'var(--color-primary)' }} />
              ) : (
                <ArrowUpDown size={14} style={{ display: 'inline', marginLeft: '4px', opacity: 0.3 }} />
              )
            )}
          </span>
          {sortable && (
            <div style={{ position: 'relative' }}>
              <Filter 
                size={14} 
                style={{ cursor: 'pointer', color: filterConfig[key] ? 'var(--color-primary)' : 'var(--color-text-muted)' }} 
                onClick={() => setShowFilterPopover(showFilterPopover === key ? null : key)}
              />
              {showFilterPopover === key && (
                <div style={{ position: 'absolute', top: '100%', left: 0, marginTop: '0.5rem', padding: '0.5rem', backgroundColor: 'var(--color-bg-card)', borderRadius: 'var(--radius-md)', boxShadow: 'var(--shadow-lg)', border: '1px solid var(--color-border)', zIndex: 50 }}>
                  <input 
                    type="text" 
                    placeholder={t('staffMgt.filter')} 
                    value={filterConfig[key] || ''}
                    onChange={(e) => setFilterConfig({...filterConfig, [key]: e.target.value})}
                    style={{ padding: '0.25rem 0.5rem', borderRadius: '4px', border: '1px solid var(--color-border)', outline: 'none', fontSize: '0.875rem' }}
                    onClick={(e) => e.stopPropagation()}
                  />
                </div>
              )}
            </div>
          )}
        </div>
      </th>
    );
  };

  const filteredUsers = users.filter(u => {
    const matchesSearch = u.displayName.toLowerCase().includes(searchTerm.toLowerCase()) || 
                          u.id.toLowerCase().includes(searchTerm.toLowerCase());
    if (!matchesSearch) return false;

    if (activeTab !== 'all' && u.role !== activeTab) return false;

    // Apply column filters
    for (const key in filterConfig) {
      if (filterConfig[key]) {
        let val = '';
        if (key === 'name') val = String(u.displayName || '');
        else if (key === 'email') val = String(u.email || '');
        else if (key === 'role') val = String(u.role || '');
        else if (key === 'jobRole') val = String(u.jobRole || '');
        else if (key === 'rank') val = String(u.rank || '');
        else if (key === 'gender') val = String(u.gender || '');
        else if (key === 'initial') val = String(u.initial || '');

        if (!val.toLowerCase().includes(filterConfig[key].toLowerCase())) return false;
      }
    }
    return true;
  });

  const sortedUsers = [...filteredUsers].sort((a, b) => {
    if (!sortConfig.key) return 0;
    
    let aVal = '';
    if (sortConfig.key === 'name') aVal = String(a.displayName || '');
    else if (sortConfig.key === 'email') aVal = String(a.email || '');
    else if (sortConfig.key === 'role') aVal = String(a.role || '');
    else if (sortConfig.key === 'jobRole') aVal = String(a.jobRole || '');
    else if (sortConfig.key === 'rank') aVal = String(a.rank || '');
    else if (sortConfig.key === 'gender') aVal = String(a.gender || '');
    else if (sortConfig.key === 'initial') aVal = String(a.initial || '');
    
    let bVal = '';
    if (sortConfig.key === 'name') bVal = String(b.displayName || '');
    else if (sortConfig.key === 'email') bVal = String(b.email || '');
    else if (sortConfig.key === 'role') bVal = String(b.role || '');
    else if (sortConfig.key === 'jobRole') bVal = String(b.jobRole || '');
    else if (sortConfig.key === 'rank') bVal = String(b.rank || '');
    else if (sortConfig.key === 'gender') bVal = String(b.gender || '');
    else if (sortConfig.key === 'initial') bVal = String(b.initial || '');

    if (aVal.toLowerCase() < bVal.toLowerCase()) return sortConfig.direction === 'asc' ? -1 : 1;
    if (aVal.toLowerCase() > bVal.toLowerCase()) return sortConfig.direction === 'asc' ? 1 : -1;
    return 0;
  });

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', flexDirection: 'column', gap: '1rem', color: 'var(--color-text-muted)' }}>
        <Loader size={32} className="animate-spin" />
        <p>{t('staffMgt.loading')}</p>
      </div>
    );
  }

  return (
    <div className="animate-fade-in new-theme" style={{ width: '100%' }}>
      
      {/* Tabs */}
      <div className="theme-tabs-bar">
        <div className="theme-tabs-group">
          {[
            { id: 'all', label: t('staffMgt.tabs.all'), icon: Users },
            { id: ROLE.SYSTEM_ADMIN, label: t('staffMgt.tabs.admins'), icon: Shield },
            { id: ROLE.TEACHER, label: t('staffMgt.tabs.teachers'), icon: BookOpen },
            { id: ROLE.NON_TEACHING, label: t('staffMgt.tabs.supporting'), icon: Briefcase },
          ].map(tab => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`theme-tab ${isActive ? 'active' : ''}`}
              >
                <div className="theme-tab-icon">
                  <Icon size={20} />
                </div>
                {tab.label}
              </button>
            )
          })}
        </div>

        <HeaderActionsPortal>
          <div style={{ display: 'flex', gap: '1rem', padding: '0.5rem 0' }}>
            <div style={{ position: 'relative' }}>
              <Search size={18} style={{ position: 'absolute', left: '0.75rem', top: '50%', transform: 'translateY(-50%)', color: 'var(--color-text-muted)' }} />
              <input 
                type="text" 
                placeholder={t('staffMgt.search')} 
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                style={{
                  padding: '0.75rem 1rem 0.75rem 2.5rem',
                  borderRadius: 'var(--radius-md)',
                  border: '1px solid var(--color-border)',
                  outline: 'none',
                  minWidth: '250px'
                }}
              />
            </div>
            <button className="btn-outline" onClick={exportUsersCSV} title={t('staffMgt.exportCsv')} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <Download size={18} />
            </button>
            <label className="btn-outline" title={t('staffMgt.importCsv')} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', margin: 0 }}>
              <Upload size={18} />
              <input type="file" accept=".csv" style={{ display: 'none' }} onChange={handleImportCSV} />
            </label>
            {selectedUsers.length > 0 ? (
              <button className="btn-primary" onClick={handleBatchDelete} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', backgroundColor: 'var(--color-danger)' }}>
                <Trash2 size={18} /> Delete Selected ({selectedUsers.length})
              </button>
            ) : (
              <button className="btn-primary" onClick={handleOpenAddUser} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <Plus size={18} /> Add
              </button>
            )}
          </div>
        
        </HeaderActionsPortal>
      </div>

      {/* Content Area */}
      <div style={{ flex: 1, backgroundColor: 'var(--color-bg-card)', borderRadius: isMobile ? 0 : 'var(--radius-lg)', boxShadow: isMobile ? 'none' : 'var(--shadow-sm)', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        
        {true ? (
          <div style={{ flex: 1, overflowY: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
              <thead style={{ position: 'sticky', top: 0, backgroundColor: 'var(--color-bg-app)', zIndex: 10 }}>
                <tr>
                  <th style={{ padding: '1rem 1.5rem', borderBottom: '1px solid var(--color-border)', width: '40px' }}>
                    <input type="checkbox" checked={selectedUsers.length > 0 && selectedUsers.length === sortedUsers.length} onChange={toggleSelectAll} style={{ cursor: 'pointer' }} />
                  </th>
                  {renderHeader(t('staffMgt.name'), 'name')}
                  {renderHeader(t('staffMgt.email'), 'email')}
                  {renderHeader(t('staffMgt.role'), 'role')}
                  {renderHeader(t('staffMgt.jobRole'), 'jobRole')}
                  {renderHeader(t('staffMgt.rank'), 'rank')}
                  {renderHeader(t('staffMgt.gender'), 'gender')}
                  {renderHeader(t('staffMgt.initial'), 'initial')}
                  <th style={{ padding: '1rem 1.5rem', borderBottom: '1px solid var(--color-border)', fontWeight: '600', color: 'var(--color-text-muted)', textAlign: 'right' }}>{t('staffMgt.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {sortedUsers.length === 0 ? (
                  <tr>
                    <td colSpan="9" style={{ padding: '2rem', textAlign: 'center', color: 'var(--color-text-muted)' }}>{t('staffMgt.noUsers')}</td>
                  </tr>
                ) : (
                  sortedUsers.map(user => (
                    <tr key={user.id} style={{ borderBottom: '1px solid var(--color-border)', backgroundColor: selectedUsers.includes(user.id) ? 'rgba(var(--color-primary-rgb), 0.05)' : 'transparent' }}>
                      <td style={{ padding: '1rem 1.5rem' }}>
                        <input type="checkbox" checked={selectedUsers.includes(user.id)} onChange={() => toggleSelectUser(user.id)} style={{ cursor: 'pointer' }} />
                      </td>
                      <td style={{ padding: '1rem 1.5rem', fontWeight: '600', color: 'var(--color-text-main)' }}>
                          {user.displayName}
                        </td>
                        <td style={{ padding: '1rem 1.5rem', fontSize: '0.875rem', color: 'var(--color-text-muted)' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            {user.email} {user.id !== user.email && <span style={{ fontSize: '0.7rem', opacity: 0.6 }}>({user.id})</span>}
                            {user.hasLoginAccess && <span title={t('staffMgt.loginEnabled')}>🔑</span>}
                          </div>
                        </td>
                        <td style={{ padding: '1rem 1.5rem' }}>
                        <span style={{ 
                          padding: '0.25rem 0.5rem', 
                          borderRadius: '4px', 
                          fontSize: '0.75rem', 
                          fontWeight: '700',
                          backgroundColor: user.role === 'admin' ? 'var(--color-danger-bg)' : user.role === 'teacher' ? 'var(--color-info-bg)' : 'var(--color-bg-subtle)',
                          color: user.role === 'admin' ? 'var(--color-danger-text)' : user.role === 'teacher' ? 'var(--color-info-text)' : 'var(--color-text-subtle)',
                          textTransform: 'uppercase'
                        }}>
                          {user.role}
                        </span>
                        {user.subRoles.length > 0 && (
                          <div style={{ marginTop: '0.5rem', fontSize: '0.75rem', color: 'var(--color-text-muted)' }}>
                            {user.subRoles.join(', ').replace(/_/g, ' ')}
                          </div>
                        )}
                      </td>
                        <td style={{ padding: '1rem 1.5rem', fontSize: '0.875rem' }}>{user.jobRole || 'N/A'}</td>
                        <td style={{ padding: '1rem 1.5rem', fontSize: '0.875rem' }}>{user.rank || 'N/A'}</td>
                        <td style={{ padding: '1rem 1.5rem', fontSize: '0.875rem' }}>{user.gender || 'N/A'}</td>
                        <td style={{ padding: '1rem 1.5rem', fontSize: '0.875rem' }}>{user.initial || 'N/A'}</td>
                        <td style={{ padding: '1rem 1.5rem', textAlign: 'right' }}>
                        <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                          <button className="btn-outline" onClick={() => handleOpenEditUser(user)} style={{ padding: '0.5rem' }} title={t('staffMgt.editUser')}>
                            <Edit2 size={16} />
                          </button>
                          <button className="btn-outline" onClick={() => handleDeleteUser(user.id)} style={{ padding: '0.5rem', color: 'var(--color-danger)', borderColor: 'var(--color-danger)' }} title={t('staffMgt.deleteUser')}>
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        ) : (
          <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--color-text-muted)', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1rem' }}>
            <LayoutGrid size={48} style={{ opacity: 0.5 }} />
            <p>{t('staffMgt.batchMigrating')}</p>
          </div>
        )}
      </div>
      {showUserModal && createPortal(
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'var(--color-overlay)', zIndex: 100, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <div style={{ backgroundColor: 'var(--color-bg-card)', padding: '2rem', borderRadius: 'var(--radius-lg)', width: '100%', maxWidth: '600px', maxHeight: '90vh', overflowY: 'auto' }}>
            <h3 style={{ fontSize: '1.25rem', fontWeight: '700', marginBottom: '1.5rem' }}>
              {isEditingUser ? t('staffMgt.editAccount') : t('staffMgt.addAccount')}
            </h3>
            <form onSubmit={handleSaveUser} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              
              <div style={{ display: 'flex', gap: '1rem' }}>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.5rem' }}>{t('staffMgt.emailLabel')}</label>
                  <input type="email" className="input-field" value={userForm.email} onChange={e => setUserForm({...userForm, email: e.target.value})} disabled={isEditingUser} placeholder={t('staffMgt.emailPlaceholder')} required />
                  {isEditingUser && <span style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)' }}>{t('staffMgt.emailFixed')}</span>}
                </div>
              </div>

              <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', backgroundColor: 'var(--color-bg-app)', padding: '1rem', borderRadius: 'var(--radius-md)' }}>
                <input 
                  type="checkbox" 
                  id="hasLoginAccess"
                  checked={userForm.hasLoginAccess} 
                  onChange={e => setUserForm({...userForm, hasLoginAccess: e.target.checked})} 
                  style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                />
                <label htmlFor="hasLoginAccess" style={{ fontWeight: '600', cursor: 'pointer', margin: 0 }}>{t('staffMgt.enableLogin')}</label>
              </div>

              {userForm.hasLoginAccess && (
                <div style={{ display: 'flex', gap: '1rem' }}>
                  <div style={{ flex: 1 }}>
                    <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.5rem' }}>
                      {t('staffMgt.password')} {isEditingUser && t('staffMgt.passwordOverride')}
                    </label>
                    <input 
                      type="password" 
                      className="input-field" 
                      value={userForm.password} 
                      onChange={e => setUserForm({...userForm, password: e.target.value})} 
                      placeholder={isEditingUser ? t('staffMgt.passwordKeep') : '••••••••'} 
                      required={!isEditingUser} 
                    />
                    {isEditingUser && <span style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)' }}>{t('staffMgt.passwordForcesChange')}</span>}
                  </div>
                </div>
              )}

              <div style={{ display: 'flex', gap: '1rem' }}>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.5rem' }}>{t('staffMgt.lastName')}</label>
                  <input type="text" className="input-field" value={userForm.lastName} onChange={e => setUserForm({...userForm, lastName: e.target.value})} placeholder={t('staffMgt.lastNamePlaceholder')} required />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.5rem' }}>{t('staffMgt.firstName')}</label>
                  <input type="text" className="input-field" value={userForm.firstName} onChange={e => setUserForm({...userForm, firstName: e.target.value})} placeholder={t('staffMgt.firstNamePlaceholder')} required />
                </div>
              </div>

              <div style={{ display: 'flex', gap: '1rem' }}>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.5rem' }}>{t('staffMgt.chineseName')}</label>
                  <input type="text" className="input-field" value={userForm.chineseName} onChange={e => setUserForm({...userForm, chineseName: e.target.value})} placeholder={t('staffMgt.chineseNamePlaceholder')} />
                </div>
              </div>

              <div style={{ display: 'flex', gap: '1rem' }}>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.5rem' }}>{t('staffMgt.accessLevel')}</label>
                  <select className="input-field" value={userForm.systemRole} onChange={e => setUserForm({...userForm, systemRole: e.target.value})}>
                    {ASSIGNABLE_ROLES.map(r => (
                      <option key={r} value={r}>{roleWord(r)}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div style={{ display: 'flex', gap: '1rem' }}>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.5rem' }}>{t('staffMgt.gender')}</label>
                  <select className="input-field" value={userForm.gender} onChange={e => setUserForm({...userForm, gender: e.target.value})}>
                    <option value="Male">{t('staffMgt.genders.male')}</option>
                    <option value="Female">{t('staffMgt.genders.female')}</option>
                  </select>
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.5rem' }}>{t('staffMgt.staffInitial')}</label>
                  <input type="text" className="input-field" value={userForm.initial} onChange={e => setUserForm({...userForm, initial: e.target.value})} placeholder={t('staffMgt.staffInitialPlaceholder')} />
                </div>
              </div>

              <div style={{ display: 'flex', gap: '1rem' }}>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.5rem' }}>{t('staffMgt.jobRole')}</label>
                  <select className="input-field" value={userForm.jobRole} onChange={(e) => setUserForm({...userForm, jobRole: e.target.value})}>
                    <option value="">{t('staffMgt.selectJobRole')}</option>
                    {(dropdownConfig.jobRoles || []).filter(r => {
                      const val = typeof r === 'string' ? r : (r.code || r.nameEn || r.name);
                      return r.active !== false || val === userForm.jobRole;
                    }).map(r => {
                      const val = typeof r === 'string' ? r : (r.code || r.nameEn || r.name);
                      const label = typeof r === 'string' ? r : (r.nameEn || r.name);
                      return <option key={val} value={val}>{label}</option>;
                    })}
                  </select>
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.5rem' }}>{t('staffMgt.edbRank')}</label>
                  <select className="input-field" value={userForm.rank} onChange={(e) => setUserForm({...userForm, rank: e.target.value})}>
                    <option value="">{t('staffMgt.selectRank')}</option>
                    {(dropdownConfig.edbRanks || []).filter(r => {
                      const val = typeof r === 'string' ? r : (r.code || r.nameEn || r.name);
                      return r.active !== false || val === userForm.rank;
                    }).map(r => {
                      const val = typeof r === 'string' ? r : (r.code || r.nameEn || r.name);
                      const label = typeof r === 'string' ? r : (r.nameEn || r.name);
                      return <option key={val} value={val}>{label}</option>;
                    })}
                  </select>
                </div>
              </div>

              <div style={{ display: 'flex', gap: '1rem', marginTop: '1rem' }}>
                <button type="button" className="btn-outline" style={{ flex: 1 }} onClick={() => setShowUserModal(false)}>{t('common.cancel')}</button>
                <button type="submit" className="btn-primary" style={{ flex: 1 }}>{isEditingUser ? t('staffMgt.saveChanges') : t('staffMgt.createAccount')}</button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default StaffMgt;
